import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { createCampaignDetails, getCampaignUsers } from '../../../api/campaign';

const RecipientPickerDialog = ({ open, campaignId, onClose, onInserted }) => {
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [search, setSearch] = useState('');

  async function loadUsers() {
    const result = await getCampaignUsers({
      campaignId,
      allUsers: showAllUsers,
    });

    setUsers(result);
  }

  useEffect(() => {
    if (open && campaignId) {
      loadUsers();
    }
  }, [open, campaignId, showAllUsers]);

  const filteredUsers = useMemo(() => {
    const needle = search.toLowerCase();

    return users.filter((user) => {
      const value = `${user.text ?? ''} ${user.email ?? ''}`.toLowerCase();
      return value.includes(needle);
    });
  }, [users, search]);

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }

      return [...prev, userId];
    });
  };

  const handleInsert = async () => {
    await Promise.all(
      selectedUserIds.map((userId) =>
        createCampaignDetails(campaignId, userId),
      ),
    );

    setSelectedUserIds([]);
    onInserted();
  };

  const allVisibleSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedUserIds.includes(user.id_user));

  const toggleAllVisible = () => {
    const visibleIds = filteredUsers.map((user) => user.id_user);

    setSelectedUserIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleIds.includes(id));
      }

      return Array.from(new Set([...prev, ...visibleIds]));
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Címzettek kiválasztása</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent='space-between'
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              size='small'
              label='Keresés név vagy e-mail alapján'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={showAllUsers}
                  onChange={(e) => setShowAllUsers(e.target.checked)}
                />
              }
              label='Összes felhasználó'
            />
          </Stack>

          <Typography variant='body2' color='text.secondary'>
            Kijelölve: {selectedUserIds.length} felhasználó
          </Typography>

          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'>
                  <Checkbox
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                  />
                </TableCell>
                <TableCell>Felhasználó</TableCell>
                <TableCell>e-Mail cím</TableCell>
                <TableCell>APP aktív</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow
                  key={user.id_user}
                  hover
                  onClick={() => toggleUser(user.id_user)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={selectedUserIds.includes(user.id_user)}
                      onChange={() => toggleUser(user.id_user)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>

                  <TableCell>{user.text}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {Number(user.registration_approved) === 1 ? 'Igen' : 'Nem'}
                  </TableCell>
                </TableRow>
              ))}

              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    Nincs találat a megadott feltételekkel.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Bezár</Button>

        <Button
          variant='contained'
          onClick={handleInsert}
          disabled={selectedUserIds.length === 0}
        >
          Hozzáadás
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipientPickerDialog;
